import { useEffect, useMemo, useRef, useState } from "react";
import { useCatalogue, displayName } from "../site/useCatalogue";
import ProductCard from "../site/ProductCard";
import QuickView from "../site/QuickView";
import { W2 } from "../site/fx";
import { useSEO } from "../seo";

const FINISHES = ["Mirror", "Satin/Brushed", "Hammered", "Gold", "Copper-tone"];
const CAPACITIES = ["1 L", "1.5 L", "3 L", "4 L", "5 L", "8 L", "10 L", "12 L"];
const CONFIGS = ["Single", "Double", "Triple"];

export default function Products() {
  useSEO("Products — Sepla Hotelware", "Browse all 46 stainless steel hotelware products: cereal and juice dispensers, buffet service, ice & bar, bowls and tabletop.");
  const { categories, products } = useCatalogue();
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [chips, setChips] = useState({ finish: [], capacity: [], configuration: [] });
  const [quick, setQuick] = useState(null);
  const tabsRef = useRef(null);

  // sliding underline follows the active tab
  useEffect(() => {
    const wrap = tabsRef.current;
    if (!wrap) return;
    const btn = wrap.querySelector("button.on");
    const bar = wrap.querySelector(".ptabs-underline");
    if (btn && bar) {
      bar.style.left = `${btn.offsetLeft}px`;
      bar.style.width = `${btn.offsetWidth}px`;
    }
  }, [tab, categories]);

  const toggleChip = (kind, val) =>
    setChips((c) => ({ ...c, [kind]: c[kind].includes(val) ? c[kind].filter((x) => x !== val) : [...c[kind], val] }));

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return products.filter((p) => {
      if (tab !== "all" && p.category !== tab) return false;
      if (needle) {
        const hay = [p.name, p.code, ...(p.variants || []).map((v) => v.code)].join(" ").toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      if (chips.finish.length && !chips.finish.includes(p.finish)) return false;
      if (chips.configuration.length) {
        const cfg = p.configuration || "";
        if (!chips.configuration.some((cf) => cfg.toLowerCase().includes(cf.toLowerCase()))) return false;
      }
      if (chips.capacity.length) {
        const cap = `${p.capacity || ""} ${(p.variants || []).map((v) => v.size).join(" ")}`;
        if (!chips.capacity.some((cp) => cap.includes(cp))) return false;
      }
      return true;
    });
  }, [products, tab, q, chips]);

  const activeCat = categories.find((c) => c.slug === tab);

  return (
    <main className="page" data-testid="products-page">
      <header className="page-head">
        <p className="kicker">The full range</p>
        <W2 as="h1" parts={[{ t: "Every piece, " }, { t: "food-grade", em: true }, { t: " steel" }]} className="page-title" />
        <p className="page-sub">Filter by category, finish, capacity or configuration — or search a product name or code.</p>
      </header>

      <div className="ptabs" data-testid="category-tabs" ref={tabsRef}>
        <button className={tab === "all" ? "on" : ""} onClick={() => setTab("all")} data-testid="tab-all">All</button>
        {categories.map((c) => (
          <button key={c.slug} className={tab === c.slug ? "on" : ""} onClick={() => setTab(c.slug)} data-testid={`tab-${c.slug}`}>
            {displayName(c.name)}
          </button>
        ))}
        <span className="ptabs-underline" aria-hidden="true" />
      </div>

      <div className="ptools">
        <input
          className="psearch"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name or code — try “SP-JD”"
          data-testid="product-search"
        />
        <div className="chips" data-testid="filter-chips">
          <span className="chips-label">Finish</span>
          {FINISHES.map((f) => (
            <button key={f} className={"chip" + (chips.finish.includes(f) ? " on" : "")} onClick={() => toggleChip("finish", f)} data-testid={`chip-finish-${f}`}>{f}</button>
          ))}
          <span className="chips-label">Capacity</span>
          {CAPACITIES.map((f) => (
            <button key={f} className={"chip" + (chips.capacity.includes(f) ? " on" : "")} onClick={() => toggleChip("capacity", f)} data-testid={`chip-cap-${f}`}>{f}</button>
          ))}
          <span className="chips-label">Config</span>
          {CONFIGS.map((f) => (
            <button key={f} className={"chip" + (chips.configuration.includes(f) ? " on" : "")} onClick={() => toggleChip("configuration", f)} data-testid={`chip-config-${f}`}>{f}</button>
          ))}
        </div>
      </div>

      {activeCat && <p className="pcount">{activeCat.num} — {displayName(activeCat.name)} · {filtered.length} products</p>}
      {!activeCat && <p className="pcount">{filtered.length} products</p>}

      <div className="pgrid" data-testid="product-grid">
        {filtered.map((p) => <ProductCard key={p.code} product={p} onQuickView={setQuick} />)}
        {filtered.length === 0 && <p className="pempty">Nothing matches — clear a filter or try another code.</p>}
      </div>

      <QuickView product={quick} onClose={() => setQuick(null)} />
    </main>
  );
}
